import { defineMessages } from 'react-intl';

export default defineMessages({
  // Core visitor and visit metrics
  visitors: 'Visitors',
  visitorsDescription: 'The unique number of visitors.',
  visits: 'Visits',
  visitsDescription: 'The number of visits. If a visitor is inactive for more than 30 minutes, it is counted as a new visit.',
  newVisitors: 'New visitors',
  newVisitorsDescription: 'The number of visitors where this is their first visit.',
  returningVisitors: 'Returning visitors',
  returningVisitorsDescription: 'The number of visitors that are returning after a previous visit.',
  avgVisitCount: 'Average visit count',
  avgVisitCountDescription: 'The average number of visits per visitor.',

  pageviews: 'Page views',
  pageviewsDescription: 'The number of page views. If a visitor reloads the page, it is counted as a new page view.',
  uniquePageviews: 'Unique page views',
  uniquePageviewsDescription: 'The number of unique page views, counting only the first view of each page per visit.',

  // Event and interaction metrics
  visitEvents: 'Total events',
  visitEventsDescription: 'The total number of events triggered during visits.',
  visitInteractions: 'Total interactions',
  visitInteractionsDescription: 'The total number of interactions during visits.',
  visitSiteSearches: 'Total searches',
  visitSiteSearchesDescription: 'The total number of searches performed during visits.',
  visitTotalActions: 'Total actions',
  visitTotalActionsDescription: 'The total number of actions performed during visits.',
  visitAvgActions: 'Average actions',
  visitAvgActionsDescription: 'The average number of actions performed during a visit.',
  visitMaxActions: 'Max actions',
  visitMaxActionsDescription: 'The maximum number of actions performed during a visit.',

  // Action-specific metrics
  events: 'Events',
  eventsDescription: 'The number of events.',
  eventTotalValue: 'Total event value',
  eventTotalValueDescription: 'The total of all event values.',
  eventAvgValue: 'Average event value',
  eventAvgValueDescription: 'The average of all event values.',
  eventMaxValue: 'Max event value',
  eventMaxValueDescription: 'The maximum of all event values.',
  eventMinValue: 'Min event value',
  eventMinValueDescription: 'The minimum of all event values.',

  siteSearches: 'Site searches',
  siteSearchesDescription: 'The number of site searches performed.',
  uniqueSiteSearches: 'Unique site searches',
  uniqueSiteSearchesDescription: 'The number of unique site searches performed.',
  siteSearchResults: 'Site search results',
  siteSearchResultsDescription: 'The number of site search results returned.',
  siteSearchPages: 'Site search pages',
  siteSearchPagesDescription: 'The number of site search result pages visited.',
  siteSearchAvgResults: 'Average site search results',
  siteSearchAvgResultsDescription: 'The average number of site search results returned.',
  siteSearchExitRate: 'Site search exit rate',
  siteSearchExitRateDescription: 'The percentage of visits that existed the site after a site search.',
  pageviewsFollowingSiteSearch: 'Search pageviews',
  pageviewsFollowingSiteSearchDescription: 'The number of pageviews following a site search.',
  pageviewsFollowingSiteSearchRate: 'Search rate',
  pageviewsFollowingSiteSearchRateDescription: 'The percentage of pageviews following a site search of all pageviews.',

  outlinks: 'Outbound links',
  outlinksDescription: 'The number of outbound links clicked.',
  uniqueOutlinks: 'Unique outbound links',
  uniqueOutlinksDescription: 'The number of unique outbound links clicked.',
  downloads: 'Downloads',
  downloadsDescription: 'The number of file downloads.',
  uniqueDownloads: 'Unique downloads',
  uniqueDownloadsDescription: 'The number of unique files downloaded.',

  // Time-based metrics
  totalVisitTime: 'Total visit time',
  totalVisitTimeDescription: 'The total time spent across all visits.',
  avgVisitTime: 'Average visit time',
  avgVisitTimeDescription: 'The average time spent per visit.',
  totalTimeSpent: 'Total time spent',
  totalTimeSpentDescription: 'The total time spent across all actions.',
  avgTimeSpent: 'Average time spent',
  avgTimeSpentDescription: 'The average time spent per action.',

  // Performance metrics
  avgDomCompletion: 'Avg. DOM complete',
  avgDomCompletionDescription: 'The average time until the DOM is completely loaded.',
  avgDomProcessing: 'Avg. DOM processing',
  avgDomProcessingDescription: 'The average time spent processing the DOM.',
  avgNetworkTime: 'Avg. network time',
  avgNetworkTimeDescription: 'The average time spent on network requests.',
  avgServerTime: 'Avg. server time',
  avgServerTimeDescription: 'The average server response time.',
  avgTransferTime: 'Avg. transfer time',
  avgTransferTimeDescription: 'The average time spent transferring data.',
  avgTimeOnLoad: 'Avg. time on load',
  avgTimeOnLoadDescription: 'The average time spent on load.',
  avgPageLoadTime: 'Avg. page load time',
  avgPageLoadTimeDescription: 'The average total page load time.',

  // Conversion and behavior metrics
  bounces: 'Bounces',
  bouncesDescription: 'The number of single-page visits where the user left without any interaction.',
  bounceRate: 'Bounce rate',
  bounceRateDescription: 'The percentage of visits that were bounces (single-page visits with no interaction).',
  exits: 'Exits',
  exitsDescription: 'The number of visits that exited on a specific page.',
  exitRate: 'Exit rate',
  exitRateDescription: 'The percentage of visits that exited on a specific page.',
  entries: 'Entries',
  entriesDescription: 'The number of visits that entered on a specific page.',
  goalConvertedVisits: 'Goal converted visits',
  goalConvertedVisitsDescription: 'The number of visits that converted on at least one goal.',
  conversionVisits: 'Conversion visits',
  conversionVisitsDescription: 'The number of visits that converted on the selected goal.',
  conversions: 'Conversions',
  conversionsDescription: 'The number of goal conversions.',
  conversionVisitors: 'Conversion visitors',
  conversionVisitorsDescription: 'The number of unique visitors who converted.',
  firstConversionDate: 'First conversion date',
  firstConversionDateDescription: 'The date and time of the earliest goal conversion on record for each row. Ignores the selected date range but respects other filters.',
  lastConversionDate: 'Last conversion date',
  lastConversionDateDescription: 'The date and time of the most recent goal conversion on record for each row. Ignores the selected date range but respects other filters.',
  conversionRate: 'Conversion rate',
  conversionRateDescription: 'The percentage of conversions out of total visits.',
  visitsBeforeConversion: 'Visits before conversion',
  visitsBeforeConversionDescription: 'The average number of visits before a goal conversion.',
  daysBeforeConversion: 'Days before conversion',
  daysBeforeConversionDescription: 'The average number of days from a visitor\'s first visit to their goal conversion.',
  revenue: 'Revenue',
  revenueDescription: 'The total revenue from goal conversions.',
  revenuePerVisit: 'Revenue per visit',
  revenuePerVisitDescription: 'The average revenue from goal conversions per visit.',

  // Survey metrics
  surveyResponses: 'Survey responses',
  surveyResponsesDescription: 'The number of survey responses.',
});
